// Device sync service: pairing, automatic background sync (interval, reconnect, after local writes),
// file upload/download, offline lease and revocation. The UI subscribes to its status.
import { newId, logDocumentAccess, SCHEMA_VERSION } from '@vero/domain';
import { APP_VERSION } from '@vero/backup';
import type { Workspace } from '@vero/store';
import {
  HttpTransport, SyncClient, leaseExpired, loadSyncState, saveSyncState, seedOutbox, queueReplacedRows, syncStatus, type SyncReport, type SyncState,
} from '@vero/sync';

export interface SyncUiState extends SyncState { running: boolean; online: boolean; last?: SyncReport | null; pending: number; conflicts: number; rejected: number; readOnly: string | null }
type Listener = (s: SyncUiState) => void;

export class SyncService {
  readonly ws: Workspace;
  private state!: SyncState;
  private client: SyncClient | null = null;
  private transport: HttpTransport | null = null;
  private listeners = new Set<Listener>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private debounce: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private last: SyncReport | null = null;
  /** set by the account service when the subscription licence has lapsed (writes stop; nothing is deleted) */
  private licenceBlock: string | null = null;
  /** 0.6: a website session in the browser — the token lives only in memory, never in storage */
  private memToken: string | null = null;

  private constructor(ws: Workspace) { this.ws = ws; }
  static async start(ws: Workspace): Promise<SyncService> {
    const s = new SyncService(ws);
    s.state = await loadSyncState(ws);
    if (!s.state.deviceId) { s.state.deviceId = newId(); await saveSyncState(ws, { deviceId: s.state.deviceId }); }
    if (s.paired) s.connect();
    s.applyLease();
    // a restore/import replaces the whole dataset (no changesets): on a paired device, queue what it added or changed
    let prev = ws.data;
    ws.subscribe((v, cs) => {
      const before = prev; prev = v.data;
      if (cs === null && s.paired && ws.outboxEnabled && s.state.seeded) { void ws.run((w, ctx) => queueReplacedRows(w, before, s.state.deviceId!, ctx.now), { origin: 'remote' }).then(() => s.schedule(1000)).catch((e) => console.warn('[sync] queue after restore', e)); return; }
      if (cs && !cs.action.startsWith('sync.') && s.paired) s.schedule(4000); else s.emit();
    });
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => s.schedule(500));
      window.addEventListener('offline', () => s.emit());
    }
    s.emit();
    return s;
  }
  get paired() { return !!(this.state.serverUrl && (this.state.token || this.memToken) && !this.state.revoked); }
  get deviceId() { return this.state.deviceId!; }
  private connect() {
    this.ws.enableOutbox(this.state.deviceId!);
    // requirement 21: every request says which data schema / app version this is (the server refuses an outdated client)
    this.transport = new HttpTransport(this.state.serverUrl!, (this.state.token || this.memToken)!, undefined, { schema: SCHEMA_VERSION, app: APP_VERSION });
    this.client = new SyncClient(this.ws, this.transport);
    if (this.timer) clearInterval(this.timer);
    this.timer = setInterval(() => void this.syncNow(), 60_000);
    this.schedule(1500);
  }
  private applyLease() {
    this.ws.readOnlyReason = this.paired && leaseExpired(this.state) ? 'lease_expired' : this.state.revoked ? 'device_revoked' : this.licenceBlock;
  }
  setLicenceBlock(reason: string | null) { if (reason === this.licenceBlock) return; this.licenceBlock = reason; this.applyLease(); this.emit(); }
  subscribe(fn: Listener) { this.listeners.add(fn); fn(this.snapshot()); return () => { this.listeners.delete(fn); }; }
  snapshot(): SyncUiState {
    return { ...this.state, token: this.state.token || this.memToken ? '••••' : null, running: this.running, online: typeof navigator === 'undefined' || navigator.onLine !== false, last: this.last, ...syncStatus(this.ws.view), readOnly: this.ws.readOnlyReason };
  }
  private emit() { const s = this.snapshot(); for (const l of this.listeners) l(s); }
  schedule(ms: number) { if (this.debounce) clearTimeout(this.debounce); this.debounce = setTimeout(() => void this.syncNow(), ms); }

  /** Pair this device with an AVRASTRO sync server (the owner's own deployment). Credentials are never stored — only the session token. */
  async pair(input: { serverUrl: string; workspaceId: string; email: string; password: string }): Promise<void> {
    const url = input.serverUrl.trim().replace(/\/+$/, '');
    if (!/^https:\/\//i.test(url) && !/^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(url)) throw new Error('https_required');
    const r = await HttpTransport.login(url, { workspaceId: input.workspaceId.trim(), email: input.email, password: input.password, deviceId: this.state.deviceId! });
    const first = !this.state.seeded;
    this.state = { ...this.state, serverUrl: url, token: r.token, userId: r.userId, role: r.role, workspaceId: r.workspaceId, workspaceName: r.workspaceName, email: input.email, leaseUntil: r.leaseUntil, revoked: false, lastError: null };
    await saveSyncState(this.ws, this.state);
    if (first) {
      await this.ws.run((v, ctx) => seedOutbox(v, this.state.deviceId!, ctx.now), { origin: 'remote' });
      this.state.seeded = true;
      await saveSyncState(this.ws, { seeded: true });
    }
    this.connect();
    this.applyLease();
    await this.syncNow();
  }
  /** Use a session obtained by signing in to an AVRASTRO account (platform) instead of the manual pairing form.
   *  `connect` is false when the plan has no multi-device sync: the session is then not used for sync. */
  async adoptSession(input: { serverUrl: string; token: string; userId: string; role: string; workspaceId: string; workspaceName: string; email: string; leaseUntil: string }, connect: boolean, o: { ephemeral?: boolean } = {}): Promise<void> {
    if (!connect) { if (this.paired && this.sessionToken === input.token) await this.unpair(); return; }
    const first = !this.state.seeded || (this.state.workspaceId != null && this.state.workspaceId !== input.workspaceId);
    this.memToken = o.ephemeral ? input.token : null;
    this.state = { ...this.state, serverUrl: input.serverUrl, token: o.ephemeral ? null : input.token, userId: input.userId, role: input.role, workspaceId: input.workspaceId, workspaceName: input.workspaceName, email: input.email, leaseUntil: input.leaseUntil, revoked: false, lastError: null, ...(first ? { lastSeq: 0, uploaded: [] } : {}) };
    await saveSyncState(this.ws, this.state);
    if (first) {
      await this.ws.run((v, ctx) => seedOutbox(v, this.state.deviceId!, ctx.now), { origin: 'remote' });
      this.state.seeded = true;
      await saveSyncState(this.ws, { seeded: true });
    }
    this.connect();
    this.applyLease();
    await this.syncNow();
  }
  /** the session token currently used for sync (account service: sign-out of the same session) */
  get sessionToken(): string | null { return this.state.token ?? this.memToken ?? null; }
  async unpair() {
    if (this.timer) clearInterval(this.timer);
    this.ws.disableOutbox();
    this.client = null; this.transport = null; this.memToken = null;
    this.state = { ...this.state, token: null, serverUrl: null, revoked: false };
    await saveSyncState(this.ws, { token: null, serverUrl: null, revoked: false });
    this.applyLease(); this.emit();
  }

  async syncNow(): Promise<SyncReport | null> {
    if (!this.client || this.running) return null;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) { this.emit(); return null; }
    this.running = true; this.emit();
    try {
      const rep = await this.client.sync();
      this.last = rep;
      if (!rep.offline) {
        const me = await this.transport!.me().catch(() => null);
        this.state = { ...(await loadSyncState(this.ws)), lastError: null, updateNeeded: null, ...(me ? { leaseUntil: me.leaseUntil, role: me.role } : {}) };
        if (this.memToken && !this.state.serverUrl) this.memToken = null; // unpaired meanwhile
        await saveSyncState(this.ws, { lastError: null, updateNeeded: null, ...(me ? { leaseUntil: me.leaseUntil, role: me.role } : {}) });
        await this.uploadFiles();
      } else {
        // exact server codes: a lapsed subscription ('licence_expired', 'plan_feature') is not a revoked device
        const revoked = ['revoked', 'expired', 'no_token'].includes(rep.error ?? '');
        // requirement 21: the server needs a newer app — sync pauses (nothing is sent or received), the outbox is kept,
        // local work goes on; the Sync screen and badge ask for the update
        const d = rep.detail ?? {};
        const updateNeeded = rep.error === 'client_outdated' ? { minSchema: typeof d.minSchema === 'number' ? d.minSchema : null, minApp: typeof d.minApp === 'string' ? d.minApp : null } : null;
        this.state = { ...this.state, lastError: rep.error ?? null, revoked: revoked || this.state.revoked, updateNeeded };
        await saveSyncState(this.ws, { lastError: rep.error ?? null, revoked: this.state.revoked, updateNeeded });
      }
      this.applyLease();
      return rep;
    } finally { this.running = false; this.emit(); }
  }

  /** Files of documents are uploaded once (content-addressed), after their document row reached the server. */
  private async uploadFiles() {
    const done = new Set(this.state.uploaded ?? []);
    const add: string[] = [];
    for (const d of this.ws.view.rows('documents')) {
      if (!d.fileId || !d.sha256 || done.has(d.sha256)) continue;
      const f = await this.ws.adapter.getFile(d.fileId);
      if (!f) continue;
      try { await this.transport!.uploadFile(d.sha256, f.content, f.meta.mime, d.id); add.push(d.sha256); } catch { /* retried next cycle */ }
    }
    if (add.length) { this.state.uploaded = [...done, ...add]; await saveSyncState(this.ws, { uploaded: this.state.uploaded }); }
  }
  /** On-demand download of a document file that another device uploaded (permission checked by the server; access logged). */
  /** `log: false` (v18): a file of the member's own private tax folder — no access event that names it in the shared audit trail */
  async fetchFile(documentId: string, o: { log?: boolean } = {}): Promise<Uint8Array | null> {
    const d = this.ws.view.get('documents', documentId);
    if (!d?.sha256 || !this.transport) return null;
    const bytes = await this.transport.downloadFile(d.sha256);
    if (d.fileId) await this.ws.adapter.putFile({ id: d.fileId, name: d.filename ?? d.title, mime: d.mime ?? 'application/octet-stream', size: bytes.byteLength, sha256: d.sha256, createdAt: new Date().toISOString() }, bytes);
    // it came from the server: never uploaded back (a colleague's shared, read-only file would be refused every cycle)
    if (!(this.state.uploaded ?? []).includes(d.sha256)) { this.state.uploaded = [...(this.state.uploaded ?? []), d.sha256]; await saveSyncState(this.ws, { uploaded: this.state.uploaded }); }
    if (o.log !== false) await this.ws.run((v, c) => logDocumentAccess(v, documentId, 'download', c));
    return bytes;
  }
}

let SERVICE: SyncService | null = null;
export const syncService = () => SERVICE;
export async function startSyncService(ws: Workspace) { SERVICE = await SyncService.start(ws); return SERVICE; }
